/**
 * Kodda referans verilen **rol kimlikleri** (tek kaynak). Roller kanonik olarak DB'dedir (analist
 * yönetir); `/me` her rolün **ID**'sini `roleIds` alanında döndürür.
 *
 * <p><b>Neden ID, neden ad değil:</b> rol adı (`ROLES.NAME_TR`) analist tarafından yönetim ekranından
 * **değiştirilebilir**. Ada bağlanan bir kural, ad değiştiği gün derleyici de test de uyarmadan
 * **sessizce** çalışmayı bırakırdı — gizlenmesi gereken bir öğe geri görünür hâle gelirdi ve kimse fark
 * etmezdi. ID ise sabittir. Bu, backend'in zaten uyguladığı kuralın (`Role.SystemRole` javadoc'u:
 * "bu roller koddan **sabit ID ile** referans verilir, `NAME_TR` string'iyle değil") istemci tarafındaki
 * karşılığıdır. Sistem rollerinin (1-7) değerleri o enum ile birebir aynıdır ve canlı DB'ye karşı
 * doğrulanmıştır. **İstisna `YONETIM_KURULU_BASKANI`:** sistem rolü değildir, Rol Yönetimi ekranından
 * açıldı ve ID'si sıradan (`SEQ_ROLES`) geldi — ortamdan ortama farklı olabilir (bkz. o girdinin notu).
 *
 * <p>Role göre ayrım **istisnadır**: görünürlük kural olarak yetkiden (grant) okunur. Yalnız grant
 * ile ayrılamayan kozmetik durumlarda kullanılır — ör. Genel Müdür hesabı aynı yetkiyi Yönetici
 * rolünden de aldığı için grant kaldırılamaz. Asıl koruma her hâlükârda backend'dedir.
 *
 * <p>Kullanım: `menu.ts` → `hideForRoleIds: [ROLE_IDS.GENEL_MUDUR]`,
 * `DeadlineRequestsPage` → "Taleplerim" sekmesinin gizlenmesi.
 */
export const ROLE_IDS = {
  /** `ROLES.ID = 5` — backend `Role.SystemRole.GENEL_MUDUR`. */
  GENEL_MUDUR: 5,
  /**
   * `ROLES.ID = 6` — backend `Role.SystemRole.GENEL_MUDUR_YARDIMCISI_VE_BASKAN`. Canlı DB'de
   * `NAME_TR` = "Genel Müdür Yardımcısı ve Genel Müdüre Direkt Bağlı Başkan" (2026-09-10'da SQLcl ile
   * doğrulandı). Görev listesinin **yönetici kolon kümesinde** Genel Müdür ile birlikte kullanılır.
   */
  GENEL_MUDUR_YARDIMCISI_VE_BASKAN: 6,
  /**
   * `ROLES.ID = 12` — Yönetim Kurulu Başkanı (`NAME_EN` = "Board Chairman"; 2026-10-14'te dev DB'den
   * SQLcl ile ölçüldü). Backend `Role.SystemRole`'da karşılığı **yoktur** — backend bu role koddan
   * atıf vermiyor, YKB'yi yetki (`DASHBOARD_YKB_SCOPE`) ve sicil parametresiyle (`YKB_REGISTER_ID`)
   * tanıyor. Yalnız menüdeki kozmetik gizleme için burada.
   *
   * <p><b>Ortama bağlı:</b> rol, Rol Yönetimi ekranından açıldı; ID'si sabit seed'den değil sıradan
   * geldi. THY ortamında ID'si **ölçülmedi** — teslimden önce orada `ROLES` tablosundan doğrulanmalı;
   * farklıysa gizleme orada çalışmaz, o ID başka bir role aitse o rolde yanlışlıkla gizlenir.
   */
  YONETIM_KURULU_BASKANI: 12,
} as const

/** Geçerli rol kimliği (union) — literal hata derlemede yakalanır. */
export type RoleId = (typeof ROLE_IDS)[keyof typeof ROLE_IDS]
